import styled from 'styled-components';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CryptoJS from "crypto-js";
import Joyride from 'react-joyride';
import InterviewLayout from "./InterviewLayout";
import { usePlayback } from "../hooks/usePlayback";
import { useMicControl } from "../hooks/useMicControl";
import { useSignedVideos } from "../hooks/useSignedVideos";
import { determineNextQuestionId, calculateInterviewProgress, sanitizeResponsesForSave, normalizeAnswerForQuestion, shouldEndAfterVideoForClip, getFinalClipKey, getFinalQuestion, getIntroClipKey } from "../utils/flowEngine";
import { requestFullscreen } from "../utils/fullscreen";
import {
  fetchPesquisaScriptVersion,
  fetchScriptDocument,
  fetchGeneratedRoteiroScriptDocument,
  fetchInterviewDocument,
  requestEvidenceUploadUrls,
  saveInterviewResponses,
  saveInterviewFinalResults,
  saveResumeTarget,
  resetInterviewState,
  sendCompletionEmails
} from "../services/interviewService";
import {
  DEFAULT_INTERACTION_PHASE,
  DEFAULT_WORKFLOW_STATUS,
  resolveStatusModelVersion
} from "../utils/interviewStatusModel";
import { getAvatarSpeech } from "../utils/avatarSpeechApi";

/**
 * ChatContainerEntrevista
 * - Renderer base utilizado para compliance/recrutamento/legados.
 */
// TODO: Extrair serviços de Firestore/Functions para `src/services/interviewService.js`.
// TODO: Extrair upload de evidências para `src/hooks/useEvidenceUpload.js`.
// TODO: Extrair controle de vídeo/playback para `src/hooks/useInterviewPlayback.js`.
// TODO: Extrair timers de inatividade/resume para `src/hooks/useInactivityTimer.js`.

const canaisDeDenuncia = {
  materdei: { site: 'www.canalconfidencial.com.br/materdei', telefone: '0800 741 0017' },
};

const normalizeAvatarId = (value = '') => {
  const base = value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return base || 'avatar';
};

const normalizePathSegment = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const buildLegacyAvatarPath = (interviewType = 'pesquisa-de-compliance', avatarId = 'avatar') =>
  `eia/media/${interviewType}/${avatarId}`;

const buildScopedAvatarPath = (interviewType = 'pesquisa-de-compliance', namespace = 'default', avatarId = 'avatar') =>
  `eia/media/${interviewType}/${namespace}/${avatarId}`;

const dedupePaths = (...paths) => {
  const seen = new Set();
  return paths
    .flat()
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => item.trim().replace(/\/+$/, ''))
    .filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const DEFAULT_INTERVIEW_FEATURES = {
  captions: true,
  speed: true,
  audio: true,
  avatarPicker: true,
};

const RS_DEFAULT_INTRO_CLIP_KEY = 'intro';
const MISSING_VIDEO_FALLBACK_CLIP_KEY = '__video_em_producao__';
const VIDEO_LOAD_WATCHDOG_MS = 12000;

const RS_DEFAULT_INTRO_TEXT =
  'Olá! Eu sou a Elza e vou conduzir sua entrevista de recrutamento e seleção. Vou fazer perguntas objetivas sobre sua experiência e seu perfil profissional. Você pode responder por texto ou áudio, como preferir. Vamos começar?';

const extractScriptFeatures = (interviewData = {}) => {
  const scriptFeatures = interviewData?.features || {};
  const avatarsFeature = scriptFeatures?.avatars || {};
  return {
    captions: scriptFeatures?.captions !== false,
    speed: scriptFeatures?.speed !== false,
    audio: scriptFeatures?.audio !== false && scriptFeatures?.audioInput !== false,
    avatarPicker:
      avatarsFeature?.enabled !== false &&
      avatarsFeature?.menu !== false &&
      scriptFeatures?.avatarPicker !== false,
  };
};

const buildDynamicInterviewConfig = ({ interviewTypeKey, companyKey, interviewData }) => {
  const safeInterviewType = interviewTypeKey || 'pesquisa-de-compliance';
  const safeCompany = normalizePathSegment(companyKey || 'default') || 'default';
  const safeDefaultCompany = 'default';
  const scriptFeatures = extractScriptFeatures(interviewData);
  const providerPath = interviewData?.providers?.video?.path || '';

  const rawAvatars = Array.isArray(interviewData?.avatars) && interviewData.avatars.length
    ? interviewData.avatars
    : (Array.isArray(interviewData?.entrevistador)
      ? interviewData.entrevistador.map((item) => ({
          id: item?.id || item?.nome,
          label: item?.label || item?.nome || item?.id,
          name: item?.name || item?.nome,
        }))
      : []);

  const dedupe = new Set();
  const avatars = rawAvatars
    .map((avatar, index) => {
      const sourceId = avatar?.id || avatar?.name || avatar?.label || avatar?.nome || ('avatar-' + (index + 1));
      const avatarId = normalizeAvatarId(sourceId);
      const label = avatar?.label || avatar?.name || avatar?.nome || ('Avatar ' + (index + 1));
      const explicitPath = avatar?.path
        ? (typeof avatar.path === 'function' ? avatar.path(safeInterviewType) : avatar.path)
        : providerPath;

      const tenantPath = buildScopedAvatarPath(safeInterviewType, safeCompany, avatarId);
      const defaultPath = buildScopedAvatarPath(safeInterviewType, safeDefaultCompany, avatarId);
      const legacyPath = buildLegacyAvatarPath(safeInterviewType, avatarId);

      const path = explicitPath || tenantPath;
      const fallbackPaths = dedupePaths(
        avatar?.fallbackPaths || [],
        explicitPath ? [tenantPath, defaultPath, legacyPath] : [defaultPath, legacyPath]
      );

      return {
        id: avatarId,
        label,
        path,
        fallbackPaths,
        aliases: avatar?.aliases || {},
      };
    })
    .filter((avatar) => {
      if (!avatar.id || dedupe.has(avatar.id)) return false;
      dedupe.add(avatar.id);
      return true;
    });

  return {
    avatars: avatars.length
      ? avatars
      : [{
          id: 'default',
          label: 'Avatar',
          path: buildScopedAvatarPath(safeInterviewType, safeCompany, 'default'),
          fallbackPaths: dedupePaths(
            buildScopedAvatarPath(safeInterviewType, safeDefaultCompany, 'default'),
            buildLegacyAvatarPath(safeInterviewType, 'default')
          ),
          aliases: {},
        }],
    features: {
      ...DEFAULT_INTERVIEW_FEATURES,
      ...scriptFeatures,
      avatarPicker: (scriptFeatures.avatarPicker !== false) && avatars.length > 1,
    },
  };
};
const normalizeScriptPayload = (
  scriptPayload = {},
  fallbackData = {},
  interviewTypeKey = 'pesquisa-de-compliance',
  flowDefinition = null
) => {
  if (!scriptPayload) return fallbackData;
  const sourceQuestions = Array.isArray(scriptPayload.questions)
    ? scriptPayload.questions
    : (scriptPayload.questions && typeof scriptPayload.questions === 'object'
      ? Object.entries(scriptPayload.questions)
        .sort(([a], [b]) => {
          const aNum = Number.parseInt(String(a).replace(/[^0-9]/g, ''), 10);
          const bNum = Number.parseInt(String(b).replace(/[^0-9]/g, ''), 10);
          if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) return aNum - bNum;
          return String(a).localeCompare(String(b));
        })
        .map(([, value]) => value)
      : []);
  const normalizeQuestion = (question, index) => ({
    ...question,
    q: question.q || question.text || '',
    video: question.video || question.id || `q${index + 1}`
  });
  const indexedQuestions = sourceQuestions.map(normalizeQuestion);
  let normalizedQuestions = indexedQuestions;
  if (flowDefinition?.blocks?.length) {
    const byId = new Map(indexedQuestions.map((question) => [question.id, question]));
    const ordered = [];
    const seen = new Set();
    flowDefinition.blocks.forEach((block) => {
      (block.questions || []).forEach((questionId) => {
        const match = byId.get(questionId);
        if (match && !seen.has(questionId)) {
          ordered.push(match);
          seen.add(questionId);
        }
      });
    });
    const remaining = indexedQuestions.filter((question) => !seen.has(question.id));
    normalizedQuestions = ordered.length ? [...ordered, ...remaining] : indexedQuestions;
  }
  const finalQuestion = getFinalQuestion(normalizedQuestions);
  const finishText =
    scriptPayload.fim || finalQuestion?.text || finalQuestion?.q || fallbackData?.fim || '';
  const resolvedType = scriptPayload.tipo || interviewTypeKey || fallbackData?.tipo || 'pesquisa-de-compliance';
  const isRecruitmentFlow = resolvedType === 'recrutamento-e-selecao';
  const avatars = Array.isArray(scriptPayload.avatars) ? scriptPayload.avatars : [];
  const normalizedAvatars = isRecruitmentFlow
    ? avatars.map((avatar, index) => {
      if (!avatar || typeof avatar !== 'object') return avatar;
      if (index !== 0) return avatar;
      return {
        ...avatar,
        video: avatar.video || RS_DEFAULT_INTRO_CLIP_KEY,
      };
    })
    : avatars;
  const interviewerList = normalizedAvatars
    .map((avatar) => avatar && (avatar.label || avatar.name || avatar.id || avatar.nome))
    .filter(Boolean)
    .map((nome) => ({ nome }));

  const resolvedIntro = isRecruitmentFlow
    ? ((scriptPayload.intro || fallbackData?.intro || '').trim() || RS_DEFAULT_INTRO_TEXT)
    : (scriptPayload.intro || fallbackData?.intro || '');

  return {
    tipo: resolvedType,
    version: scriptPayload.version || fallbackData?.version || 'v1',
    entrevistador: interviewerList.length ? interviewerList : fallbackData?.entrevistador || [],
    avatars: normalizedAvatars,
    intro: resolvedIntro,
    fim: finishText,
    entrevista: normalizedQuestions.length ? normalizedQuestions : fallbackData?.entrevista || [],
    features: scriptPayload.features || fallbackData?.features || {},
    providers: scriptPayload.providers || fallbackData?.providers || {},
    persistence: scriptPayload.persistence || fallbackData?.persistence || {},
    contract: scriptPayload.contract || fallbackData?.contract || {},
  };
};

const logFlowEvent = (label, payload = {}) => {
  console.log(`[EIA][flow] ${label}`, payload);
};

const buildFlowIndexMap = (flow = null, questions = []) => {
  if (!flow?.blocks?.length) return new Map();
  const byId = new Map(questions.map((question) => [question.id || question.video, question]));
  const indexMap = new Map();
  let idx = 0;
  flow.blocks.forEach((block) => {
    (block.questions || []).forEach((questionId) => {
      if (indexMap.has(questionId)) return;
      if (!byId.has(questionId)) return;
      indexMap.set(questionId, idx);
      idx += 1;
    });
  });
  return indexMap;
};

const findQuestionIndexByVideo = (questions = [], videoKey) => {
  if (!videoKey || !Array.isArray(questions)) return -1;
  return questions.findIndex((question, index) => {
    const id = question.id || question.video || `q${index + 1}`;
    return id === videoKey;
  });
};

const findQuestionById = (questions = [], questionId) => {
  if (!questionId || !Array.isArray(questions)) return null;
  return questions.find((question, index) => {
    const id = question.id || question.video || `q${index + 1}`;
    return id === questionId;
  });
};

const replacePlaceholders = (text, user) => {
  if (!text || !user) return text;
  if (user.nomesocial === '' || user.nomesocial === undefined) {
    return text.replace(/\{\{user.name\}\}/g, user.nome || '');
  } else {
    return text.replace(/\{\{user.name\}\}/g, user.nomesocial || '');
  }
};

const decryptParams = (encryptedData) => {
  try {
    const secretKey = "due-prd";
    const decodedData = decodeURIComponent(encryptedData);
    const decryptedBase64 = CryptoJS.AES.decrypt(decodedData, secretKey).toString(CryptoJS.enc.Utf8);
    return JSON.parse(atob(decryptedBase64));
  } catch (error) {
    console.error("Erro ao descriptografar os parâmetros da URL:", error);
    return null;
  }
};

const getParams = () => {
  const params = new URLSearchParams(window.location.search);
  const encryptedData = params.get("data");
  const pathname = window.location.pathname || '';
  const routeMatch = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
  const routeParams = routeMatch
    ? {
        company: routeMatch[1],
        interviewType: routeMatch[2],
        denuncia: routeMatch[3],
        entrevistaId: routeMatch[4],
      }
    : {};
  if (encryptedData) {
    const decryptedParams = decryptParams(encryptedData);
    if (decryptedParams) {
      return { ...routeParams, ...decryptedParams };
    }
  }
  return { ...routeParams, ...Object.fromEntries(params.entries()) };
};

export default function ChatContainerEntrevista({ user, setShowFinishContainer, interviewType }) {
  const params = useMemo(() => getParams(), []);
  const explicitScriptVersion = params.scriptVersion || '';
  const [resolvedScriptVersion, setResolvedScriptVersion] = useState('');
  const [isScriptVersionReady, setIsScriptVersionReady] = useState(!!explicitScriptVersion);
  const scriptVersion = explicitScriptVersion || resolvedScriptVersion || (isScriptVersionReady ? 'v1.1' : '');
  const companyKey = params.company?._value || params.company || user.company || 'materdei';
  const interviewTypeKey = interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance';
  const denunciaKey = params.denuncia || user.denuncia || '';
  const [interviewData, setInterviewData] = useState(() => ({
    tipo: interviewTypeKey,
    version: scriptVersion || 'v1.1',
    entrevistador: [],
    avatars: [],
    intro: '',
    fim: '',
    entrevista: [],
    flow: null,
  }));
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  useEffect(() => {
    if (!isScriptVersionReady || !scriptVersion) return;
    console.log('[EIA] Script version:', scriptVersion);
  }, [scriptVersion, isScriptVersionReady]);
  useEffect(() => {
    if (!isScriptVersionReady) return;
    console.log('[EIA] Script payload:', {
      params,
      explicitScriptVersion,
      resolvedScriptVersion
    });
  }, [params, explicitScriptVersion, resolvedScriptVersion, isScriptVersionReady]);
  const interviewConfig = useMemo(() => (
    buildDynamicInterviewConfig({
      interviewTypeKey: interviewType || interviewTypeKey,
      companyKey,
      interviewData
    })
  ), [interviewType, interviewTypeKey, companyKey, interviewData]);

  // Estados principais
  const getSaoPauloTimestamp = useCallback(() => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date()).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}-03:00`;
  }, []);

  const getSaoPauloDate = useCallback(() => new Date(getSaoPauloTimestamp()), [getSaoPauloTimestamp]);

  const [messages, setMessages] = useState([
    { text: replacePlaceholders(interviewData.intro, user), type: 'ia', first: true, time: getSaoPauloTimestamp() }
  ]);
  const [currentIndex, setCurrentIndex] = useState(0); // índice dentro de "entrevista"
  const [text, setText] = useState('');
  const [videos, setVideos] = useState({});
  const [bgVideo, setBgVideo] = useState(null);
  const [videoKey, setVideoKey] = useState(Date.now());
  const [videoSpeed, setVideoSpeed] = useState(1.25);
  const [canSendMessage, setCanSendMessage] = useState(false);
  const [isVideoPlayingState, setIsVideoPlayingState] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [missingVideoClip, setMissingVideoClip] = useState("");
  // Avatar barato do EIA (Fase 0): áudio + eventos de viseme da última fala
  // sintetizada, consumidos pelo AvatarVisemePlayer quando avatarCfg.visemeEnabled.
  const [avatarSpeech, setAvatarSpeech] = useState(null);
  const introMessageInitialized = useRef(false);
  const {
    isListening,
    isTranscribing,
    transcript,
    resetTranscript,
    toggleListening
  } = useMicControl();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [isFinishVideo, setIsFinishVideo] = useState(false);
  const [currentClip, setCurrentClip] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(DEFAULT_WORKFLOW_STATUS);
  const [interactionPhase, setInteractionPhase] = useState(DEFAULT_INTERACTION_PHASE);
  const [statusModelVersion, setStatusModelVersion] = useState(
    resolveStatusModelVersion(interviewType || 'pesquisa-de-compliance')
  );
  const [pendingFollowUpState, setPendingFollowUpState] = useState(null);
  const pendingClipKeyRef = useRef(null);
  const missingClipTimerRef = useRef(null);
  const resolvedClipSrcRef = useRef({ clip: null, avatar: null, src: null });
  const [showResumeOverlay, setShowResumeOverlay] = useState(false);
  const [shouldShowResumeOnLoad, setShouldShowResumeOnLoad] = useState(false);
  const [shouldRunTour, setShouldRunTour] = useState(true);
  const [resumeBootstrapDone, setResumeBootstrapDone] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const askedQ1Ref = useRef(false);
  const pendingFirstQuestionRef = useRef(false);
  const resumeBootstrapKeyRef = useRef('');
  const changeVideoByNameRef = useRef(null);
  const [showCaptions, setShowCaptions] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [flowDefinition, setFlowDefinition] = useState(null);
  useEffect(() => {
    const availableIds = (interviewConfig.avatars || []).map((avatar) => avatar?.id).filter(Boolean);
    if (!availableIds.length) return;
    if (!selectedAvatar || !availableIds.includes(selectedAvatar)) {
      setSelectedAvatar(availableIds[0]);
    }
  }, [interviewConfig.avatars, selectedAvatar]);

  const inactivityTimer = useRef(null);
  const inactivitySteps = useRef(0);
  const isVideoPlaying = useRef(false);
  const shouldAutoPlayOnReadyRef = useRef(false);
  const videoRef = useRef(null);
  const lastQuestionClipRef = useRef(null);

  useEffect(() => {
    if (showResumeOverlay) {
      setInteractionPhase('overlay_retomada');
      return;
    }
    if (isVideoLoading && currentClip) {
      setInteractionPhase('carregando_video');
      return;
    }
    if (isVideoPlayingState) {
      setInteractionPhase('assistindo_video');
      return;
    }
    if (canSendMessage) {
      setInteractionPhase('respondendo');
      return;
    }
    setInteractionPhase('aguardando_continuacao');
  }, [showResumeOverlay, isVideoLoading, currentClip, isVideoPlayingState, canSendMessage]);

  useEffect(() => {
    if (shouldShowResumeOnLoad) {
      setWorkflowStatus('aguardando_retomada');
    }
  }, [shouldShowResumeOnLoad]);

  useEffect(() => {
    if (!showResumeOverlay) return;
    setWorkflowStatus((prev) => (prev === 'concluido' ? prev : 'aguardando_retomada'));
  }, [showResumeOverlay]);

  const perguntas = useMemo(() => interviewData.entrevista, [interviewData]);
  // Avatar barato do EIA (Fase 0): mapa clipKey (q.video || q.id) -> texto falado
  // (q.q || q.text), usado por speakByName para saber o que sintetizar para
  // cada clipe sem precisar re-percorrer o array de perguntas a cada chamada.
  const textByClipKey = useMemo(() => {
    const map = new Map();
    (perguntas || []).forEach((q) => {
      const clipKey = q?.video || q?.id;
      const spokenText = q?.q || q?.text || '';
      if (clipKey && spokenText) map.set(clipKey, spokenText);
    });
    return map;
  }, [perguntas]);
  const flowIndexMap = useMemo(
    () => buildFlowIndexMap(flowDefinition, perguntas),
    [flowDefinition, perguntas]
  );
  const questionClipKeys = useMemo(() => {
    const keys = new Set();
    perguntas.forEach((q) => {
      if (q?.id) keys.add(q.id);
      if (q?.video) keys.add(q.video);
    });
    return keys;
  }, [perguntas]);
  const introClipKey = useMemo(() => getIntroClipKey(interviewData), [interviewData]);
  const finalClipKey = useMemo(() => getFinalClipKey(interviewData), [interviewData]);
  const finalSlug = useMemo(
    () => normalizePathSegment(companyKey || user?.company || ''),
    [companyKey, user?.company]
  );
  const isV1Script = (scriptVersion || '').toString().trim().toLowerCase() === 'v1';
  const requiredVideoKeys = useMemo(() => {
    const keys = new Set(['continue', 'continue2', 'continue3', 'simnao', MISSING_VIDEO_FALLBACK_CLIP_KEY]);
    if (introClipKey) keys.add(introClipKey);
    if (finalClipKey) keys.add(finalClipKey);
    if (finalSlug) keys.add('fim-' + finalSlug);
    keys.add('final');
    keys.add('fim');
    perguntas.forEach((question, index) => {
      const key = question?.video || question?.id || ('q' + (index + 1));
      if (key) keys.add(key);
    });
    return Array.from(keys);
  }, [introClipKey, finalClipKey, finalSlug, interviewData, perguntas, companyKey, user?.company]);

  const handleTourCallback = useCallback((data) => {
    const status = data?.status || '';
    if (status !== 'finished' && status !== 'skipped' && status !== 'closed') return;

    setTourCompleted(true);
    setShouldRunTour(false);

    if (!videoRef.current) return;
    videoRef.current.play().catch(() => {
      if (shouldShowResumeOnLoad && bgVideo) {
        setShowResumeOverlay(true);
      }
    });
  }, [shouldShowResumeOnLoad, bgVideo]);

  useEffect(() => {
    let cancelled = false;
    const loadScriptVersion = async () => {
      if (explicitScriptVersion) {
        setResolvedScriptVersion(explicitScriptVersion);
        setIsScriptVersionReady(true);
        return;
      }
      if (!denunciaKey) return;
      try {
        const version = await fetchPesquisaScriptVersion({
          companyKey,
          interviewTypeKey,
          denunciaKey
        });
        if (!cancelled && version) {
          setResolvedScriptVersion(version);
        }
        if (!cancelled) {
          setIsScriptVersionReady(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[EIA] Erro ao carregar scriptVersion da pesquisa:', err);
        }
      } finally {
        if (!cancelled) {
          setIsScriptVersionReady(true);
        }
      }
    };
    loadScriptVersion();
    return () => {
      cancelled = true;
    };
  }, [companyKey, interviewTypeKey, denunciaKey, explicitScriptVersion]);

  useEffect(() => {
    let cancelled = false;
    if (!isScriptVersionReady || !scriptVersion) return () => { cancelled = true; };

    const fetchRemoteScript = async () => {
      try {
        let rawDoc = await fetchScriptDocument({
          companyKey,
          interviewTypeKey,
          scriptVersion: scriptVersion || 'v1'
        });

        // Recrutamento: se nao houver script canônico em _meta/scripts,
        // usa o roteiro gerado do processo (roteiro.questions) como fallback operacional.
        if (!rawDoc && interviewTypeKey === 'recrutamento-e-selecao') {
          rawDoc = await fetchGeneratedRoteiroScriptDocument({
            companyKey,
            interviewTypeKey,
            denunciaKey,
            scriptVersion: scriptVersion || 'rs-mvp-v1',
          });
        }

        if (!rawDoc) {
          throw new Error(`Script nao encontrado no Firestore: ${companyKey}/${interviewTypeKey}/${scriptVersion}`);
        }
        // O resolver de config ja devolve contrato canonico em rawDoc.script/rawDoc.flow.
        const scriptPayload = rawDoc?.script || {};

        const questionsCount = Array.isArray(scriptPayload?.questions)
          ? scriptPayload.questions.length
          : 0;
        if (!questionsCount) {
          throw new Error(`Script sem perguntas no Firestore: ${companyKey}/${interviewTypeKey}/${scriptVersion}`);
        }

        const remoteFlow = rawDoc?.flow || null;

        logFlowEvent('script.loaded', {
          version: scriptVersion,
          sourceCompany: rawDoc?._scriptSourceCompany || companyKey,
          questionsCount,
          sampleQ17Video: scriptPayload?.questions?.[16]?.video || null
        });

        const normalizedScript = normalizeScriptPayload(
          scriptPayload,
          {},
          interviewTypeKey,
          remoteFlow
        );

        logFlowEvent('script.mapped', {
          version: scriptVersion,
          mappedQ17Video: normalizedScript?.entrevista?.[16]?.video || null,
          mappedQ17Text: normalizedScript?.entrevista?.[16]?.q || null
        });

        if (cancelled) return;
        setFlowDefinition(remoteFlow || null);
        setInterviewData(normalizedScript);
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar script remoto da entrevista:', err);
          setModalType('error');
          setModalMessage('Nao foi possivel carregar o roteiro da entrevista. Recarregue a pagina.');
          setShowModal(true);
        }
      } finally {
        if (!cancelled) {
          setIsScriptLoaded(true);
        }
      }
    };

    fetchRemoteScript();

    return () => {
      cancelled = true;
    };
  }, [companyKey, interviewTypeKey, scriptVersion, isScriptVersionReady, denunciaKey]);

  // Autenticação (igual ao componente base) - usando hook de vídeos assinados
  const avatarCfg = interviewConfig.avatars.find(a => a.id === selectedAvatar) || interviewConfig.avatars[0];
  const avatarPath = typeof avatarCfg.path === 'function' ? avatarCfg.path(interviewType) : avatarCfg.path;
  const fallbackPaths = typeof avatarCfg.fallbackPaths === 'function'
    ? avatarCfg.fallbackPaths(interviewType)
    : avatarCfg.fallbackPaths;
  const avatarPaths = useMemo(() => {
    const extraPaths = Array.isArray(fallbackPaths) ? fallbackPaths : [];
    return [avatarPath, ...extraPaths].filter(Boolean);
  }, [avatarPath, fallbackPaths]);
  const videoAliases = useMemo(() => (avatarCfg.aliases || {}), [avatarCfg.aliases]);
  const firstQuestionClipKey = useMemo(() => {
    const firstQuestion = perguntas?.[0];
    return firstQuestion?.video || firstQuestion?.id || '';
  }, [perguntas]);
  const introClipCandidates = useMemo(() => {
    if (!isV1Script) return [introClipKey].filter(Boolean);
    return Array.from(new Set([
      introClipKey,
      'intro',
      'pc_ola_tudo_bem_meu_nome_adele_6f2b0dd6',
      'pc_ola_tudo_bem_meu_nome_srta_63a94d19',
    ].filter(Boolean)));
  }, [isV1Script, introClipKey]);
  const activeIntroClipKey = useMemo(() => {
    if (!isV1Script) return introClipKey;
    const nonQuestionIntro = introClipCandidates.find((key) => key && key !== firstQuestionClipKey);
    return nonQuestionIntro || introClipKey;
  }, [isV1Script, introClipKey, introClipCandidates, firstQuestionClipKey]);
  const introPrefetchKeys = useMemo(() => {
    const keys = new Set();
    if (activeIntroClipKey) keys.add(activeIntroClipKey);
    introClipCandidates.forEach((key) => {
      if (key) keys.add(key);
    });
    return Array.from(keys);
  }, [activeIntroClipKey, introClipCandidates]);
  const initialVideoKeys = useMemo(() => {
    const keys = new Set();
    introPrefetchKeys.forEach((key) => {
      if (key) keys.add(key);
    });
    if (firstQuestionClipKey) keys.add(firstQuestionClipKey);
    keys.add('continue');
    keys.add('continue2');
    keys.add('continue3');
    keys.add('simnao');
    keys.add(MISSING_VIDEO_FALLBACK_CLIP_KEY);
    return Array.from(keys);
  }, [introPrefetchKeys, firstQuestionClipKey]);

  // Carrega rápido o mínimo para iniciar a entrevista.
  const signedVideosInitial = useSignedVideos(avatarPaths, { aliases: videoAliases, keys: initialVideoKeys });
  // Faz preload completo em background enquanto o usuário assiste os primeiros vídeos.
  const signedVideosFull = useSignedVideos(avatarPaths, { aliases: videoAliases, keys: requiredVideoKeys });

  // v1: introdução sempre inicia com Adele (independente do avatar selecionado no menu)
  const introAvatarCfg = useMemo(() => {
    if (!isV1Script) return avatarCfg;
    const adele = (interviewConfig.avatars || []).find((a) => normalizeAvatarId(a?.id || '') === 'adele');
    return adele || interviewConfig.avatars?.[0] || avatarCfg;
  }, [isV1Script, interviewConfig.avatars, avatarCfg]);
  const introAvatarPath = typeof introAvatarCfg?.path === 'function' ? introAvatarCfg.path(interviewType) : introAvatarCfg?.path;
  const introFallbackPaths = typeof introAvatarCfg?.fallbackPaths === 'function'
    ? introAvatarCfg.fallbackPaths(interviewType)
    : introAvatarCfg?.fallbackPaths;
  const introAvatarPaths = useMemo(() => {
    const extraPaths = Array.isArray(introFallbackPaths) ? introFallbackPaths : [];
    return [introAvatarPath, ...extraPaths].filter(Boolean);
  }, [introAvatarPath, introFallbackPaths]);
  const introAliases = useMemo(() => (introAvatarCfg?.aliases || {}), [introAvatarCfg?.aliases]);
  const introSignedVideos = useSignedVideos(isV1Script ? introAvatarPaths : null, { aliases: introAliases, keys: introPrefetchKeys });

  useEffect(() => {
    if (signedVideosInitial && Object.keys(signedVideosInitial).length) {
      setVideos((prev) => ({ ...prev, ...signedVideosInitial }));
    }
  }, [signedVideosInitial]);

  useEffect(() => {
    if (signedVideosFull && Object.keys(signedVideosFull).length) {
      setVideos((prev) => ({ ...prev, ...signedVideosFull }));
    }
  }, [signedVideosFull]);

  useEffect(() => {
    resolvedClipSrcRef.current = { clip: null, avatar: null, src: null };
  }, [selectedAvatar]);

  useEffect(() => {
    if (!currentClip) return;
    const nextSrc = videos?.[currentClip];
    if (!nextSrc) return;
    const currentLock = resolvedClipSrcRef.current;
    const sameClipAndAvatar =
      currentLock?.clip === currentClip &&
      currentLock?.avatar === selectedAvatar &&
      currentLock?.src;
    // Se ja existe src resolvido para este clipe/avatar, nao substitui no meio do preload/playback.
    if (sameClipAndAvatar) return;
    // Evita reiniciar o video em reproducao ao chegarem novos signed URLs.
    if (isVideoPlaying.current) return;
    resolvedClipSrcRef.current = { clip: currentClip, avatar: selectedAvatar, src: nextSrc };
    if (bgVideo === nextSrc) return;
    shouldAutoPlayOnReadyRef.current = true;
    setBgVideo(nextSrc);
    setVideoKey(Date.now());
  }, [selectedAvatar, currentClip, videos, bgVideo]);


  useEffect(() => {
    if (!resumeBootstrapDone) return;
    // Se ja existe um clipe alvo (inclusive em retomada), nao injeta intro como fallback.
    if (currentClip) return;

    const startFromFirstQuestion = () => {
      if (askedQ1Ref.current) return;
      askedQ1Ref.current = true;
      const nextQuestionId = determineNextQuestionId([], flowDefinition, perguntas);
      const nextIndex = nextQuestionId ? findQuestionIndexByVideo(perguntas, nextQuestionId) : 0;
      if (nextIndex >= 0) {
        advanceToQuestion(nextIndex);
      }
    };

    const sourceVideos = isV1Script ? introSignedVideos : signedVideosInitial;
    if (!sourceVideos || !Object.keys(sourceVideos).length) {
      if (!isV1Script) {
        clearTimeout(missingClipTimerRef.current);
        const missingIntroKey = activeIntroClipKey || introClipKey || 'intro';
        setCurrentClip(missingIntroKey);
        setMissingVideoClip(missingIntroKey);
        setBgVideo(null);
        setIsVideoLoading(false);
        setCanSendMessage(false);
        missingClipTimerRef.current = setTimeout(() => startFromFirstQuestion(), 1800);
      }
      return;
    }
    const resolvedIntroKey = introPrefetchKeys.find((key) => sourceVideos[key]);
    const introVideo = resolvedIntroKey ? sourceVideos[resolvedIntroKey] : null;
    if (!introVideo) {
      if (!isV1Script) {
        clearTimeout(missingClipTimerRef.current);
        const missingIntroKey = activeIntroClipKey || introClipKey || 'intro';
        setCurrentClip(missingIntroKey);
        setMissingVideoClip(missingIntroKey);
        setBgVideo(null);
        setIsVideoLoading(false);
        setCanSendMessage(false);
        missingClipTimerRef.current = setTimeout(() => startFromFirstQuestion(), 1800);
      }
      return;
    }
    shouldAutoPlayOnReadyRef.current = true;
    setBgVideo((prev) => prev || introVideo);
    setCurrentClip((prev) => prev || resolvedIntroKey);
  }, [resumeBootstrapDone, currentClip, isV1Script, introSignedVideos, signedVideosInitial, introPrefetchKeys]);

  usePlayback(videoRef, videoSpeed);

  useEffect(() => {
    if (!shouldShowResumeOnLoad || !bgVideo) return;
    setShowResumeOverlay(true);
  }, [bgVideo, shouldShowResumeOnLoad]);

  // Transcrição de áudio
  useEffect(() => {
    if (transcript) {
      setText(wordsToNumber(transcript));
    }
  }, [transcript]);

  const handleSpeedChange = (newSpeed) => {
    setVideoSpeed(newSpeed);
    if (videoRef.current) videoRef.current.playbackRate = newSpeed;
  };

  const handleVideoCanPlay = useCallback(() => {
    clearTimeout(missingClipTimerRef.current);
    setMissingVideoClip("");
    setIsVideoLoading(false);
    if (!tourCompleted) return;
    if (shouldShowResumeOnLoad) return;
    if (!shouldAutoPlayOnReadyRef.current) return;
    if (!videoRef.current) return;
    shouldAutoPlayOnReadyRef.current = false;
    videoRef.current.play().catch(() => {});
  }, [tourCompleted, shouldShowResumeOnLoad]);

  const handleVideoLoadStart = useCallback(() => {
    if (currentClip) setIsVideoLoading(true);
  }, [currentClip]);

  const handleVideoWaiting = useCallback(() => {
    if (currentClip) setIsVideoLoading(true);
  }, [currentClip]);

  const handleVideoError = useCallback(() => {
    setIsVideoLoading(false);
    setMissingVideoClip(currentClip || "video");
  }, [currentClip]);

    // Utils mensagens
  const addMessage = useCallback((text, type, meta = {}) => {
    const replaced = replacePlaceholders(text, user);
    const ts = meta.timestamp || getSaoPauloTimestamp();
    setMessages((prev) => [...prev, { text: replaced, type, timestamp: ts, ...meta }]);
  }, [user, getSaoPauloTimestamp]);

  const changeVideo = useCallback((newVideo) => {
    if (!newVideo) return;
    shouldAutoPlayOnReadyRef.current = true;
    setBgVideo(newVideo);
    setVideoKey(Date.now());
  }, []);

  // Encerrar por inatividade
  const interromperEntrevista = useCallback(async () => {
    setCanSendMessage(false);
    setShowResumeOverlay(true);
    setWorkflowStatus('aguardando_retomada');
  }, []);

  // Inatividade
  const setInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    if (!isVideoPlaying.current && canSendMessage) {
      inactivityTimer.current = setTimeout(() => {
        inactivitySteps.current += 1;
        const cont1 = videos['continue'];
        const cont2 = videos['continue2'];
        if (inactivitySteps.current === 1) {
          setBgVideo(cont1 || bgVideo);
          addMessage("Você ainda está aí?", 'ia');
        } else if (inactivitySteps.current === 2) {
          setBgVideo(cont2 || bgVideo);
          addMessage("Você pode digitar ou usar o microfone para responder.", 'ia');
        } else if (inactivitySteps.current === 3) {
          interromperEntrevista();
        }
        setInactivityTimer();
      }, 60000);
    }
  }, [addMessage, interromperEntrevista, videos, canSendMessage, bgVideo]);

  useEffect(() => {
    if (!isVideoLoading || !currentClip) return;
    const timeoutId = setTimeout(() => {
      // Evita estado preso em "Carregando vídeo..." quando o player não dispara erro/canplay.
      setIsVideoLoading(false);
      setMissingVideoClip((prev) => prev || currentClip || 'video');
      const isIntroClip = introPrefetchKeys.includes(currentClip);
      if (!isIntroClip) {
        setCanSendMessage(true);
        setInactivityTimer();
      }
    }, VIDEO_LOAD_WATCHDOG_MS);
    return () => clearTimeout(timeoutId);
  }, [isVideoLoading, currentClip, introPrefetchKeys, setInactivityTimer]);

  useEffect(() => {
    setInactivityTimer();
    return () => clearTimeout(inactivityTimer.current);
  });

  const handleVideoPlay = () => {
    if (videoRef.current) videoRef.current.playbackRate = videoSpeed;
    setCanSendMessage(false);
    if (shouldShowResumeOnLoad) {
      setShouldShowResumeOnLoad(false);
    }
    isVideoPlaying.current = true;
    setIsVideoPlayingState(true);
    clearTimeout(inactivityTimer.current);
    setShowResumeOverlay(false);
    if (currentClip && questionClipKeys.has(currentClip)) {
      lastQuestionClipRef.current = currentClip;
    }
  };

  const advanceToQuestion = useCallback((index) => {
    const qObj = perguntas[index];
    if (!qObj) return; // acabou
    setCurrentIndex(index);
    setWorkflowStatus('em_andamento');
    setShouldShowResumeOnLoad(false);
    setShowResumeOverlay(false);
    const nextVideoKey = qObj.video || '';
    const questionId = qObj.id || qObj.video || '';
    const qIndexFromFlow = flowIndexMap.get(questionId);
    const resolvedIndex = Number.isFinite(qIndexFromFlow) ? qIndexFromFlow : index;
    addMessage(qObj.q, 'ia', {
      isQuestion: true,
      qIndex: resolvedIndex,
      questionId,
      videoKey: nextVideoKey
    });
    logFlowEvent('question', {
      qIndex: resolvedIndex,
      id: qObj.id,
      video: nextVideoKey,
      text: qObj.q
    });
    if (nextVideoKey) {
      lastQuestionClipRef.current = nextVideoKey;
      if (changeVideoByNameRef.current) {
        changeVideoByNameRef.current(nextVideoKey);
      }
    }
  }, [perguntas, addMessage, flowIndexMap]);


  const startInterviewFromFlow = useCallback(() => {
    if (askedQ1Ref.current) return;
    if (!isScriptLoaded) {
      pendingFirstQuestionRef.current = true;
      return;
    }
    askedQ1Ref.current = true;
    const nextQuestionId = determineNextQuestionId([], flowDefinition, perguntas);
    const nextIndex = nextQuestionId ? findQuestionIndexByVideo(perguntas, nextQuestionId) : 0;
    if (nextIndex >= 0) {
      advanceToQuestion(nextIndex);
    }
  }, [advanceToQuestion, flowDefinition, isScriptLoaded, perguntas]);

  const handleVideoEnd = () => {
    setCanSendMessage(true);
    isVideoPlaying.current = false;
    setIsVideoPlayingState(false);

    if (shouldEndAfterVideoForClip(perguntas, currentClip, currentIndex) && !isFinishVideo) {
      endInterview('', { playFinalVideo: false, addFinalMessage: false, showFinishOnComplete: true });
      return;
    }

    if (introPrefetchKeys.includes(currentClip)) {
      startInterviewFromFlow();
      return;
    }

    if (isFinishVideo) {
      setShowFinishContainer(true);
    } else {
      setInactivityTimer();
    }
  };

  // Avatar barato do EIA (Fase 0): equivalente de changeVideoByName quando
  // avatarCfg.visemeEnabled — em vez de resolver uma URL de vídeo pré-gravado
  // já disponível em `videos`, chama a Cloud Function de cache-aside para obter
  // (ou sintetizar na hora) o áudio + eventos de viseme daquele clipe. Espelha
  // a mesma sequência de estados de changeVideoByName (loading -> resolve ->
  // currentClip/videoKey) e o mesmo tratamento de "clipe ausente" (fallback +
  // timer de intro), só que "ausente" aqui significa "sem texto conhecido para
  // esse clipKey" em vez de "sem src de vídeo".
  const speakByName = useCallback(async (name) => {
    const spokenText = textByClipKey.get(name);
    if (!spokenText) {
      pendingClipKeyRef.current = null;
      clearTimeout(missingClipTimerRef.current);
      setMissingVideoClip(name || 'video');
      setAvatarSpeech(null);
      setBgVideo(null);
      setVideoKey(Date.now());
      setCurrentClip(name);
      setIsVideoLoading(false);

      const isIntroMissing = introPrefetchKeys.includes(name);
      if (isIntroMissing) {
        setCanSendMessage(false);
        missingClipTimerRef.current = setTimeout(() => {
          startInterviewFromFlow();
        }, 1800);
      } else {
        setCanSendMessage(true);
        setInactivityTimer();
      }
      return;
    }

    setMissingVideoClip('');
    shouldAutoPlayOnReadyRef.current = true;
    setIsVideoLoading(true);
    setCurrentClip(name);
    setVideoKey(Date.now());

    try {
      const cacheKey = /^[a-zA-Z0-9_-]+$/.test(name || '') ? name : null;
      const result = await getAvatarSpeech({
        interviewType: interviewTypeKey,
        company: companyKey,
        avatarId: avatarCfg.id,
        key: cacheKey,
        text: spokenText,
        voiceId: avatarCfg.voiceId,
      });
      setAvatarSpeech(result);
    } catch (error) {
      console.warn('[EIA] Falha ao sintetizar fala do avatar:', name, error?.message || error);
      setAvatarSpeech(null);
      setIsVideoLoading(false);
      setMissingVideoClip(name || 'video');
      setCanSendMessage(true);
      setInactivityTimer();
    }
  }, [textByClipKey, introPrefetchKeys, startInterviewFromFlow, setInactivityTimer, interviewTypeKey, companyKey, avatarCfg]);

  const changeVideoByName = useCallback((name) => {
    if (avatarCfg?.visemeEnabled) {
      speakByName(name);
      return;
    }
    resolvedClipSrcRef.current = { clip: name || null, avatar: selectedAvatar, src: null };
    const src = videos?.[name];
    const fallbackSrc = videos?.[MISSING_VIDEO_FALLBACK_CLIP_KEY];
    if (!src) {
      if (fallbackSrc && name !== MISSING_VIDEO_FALLBACK_CLIP_KEY) {
        clearTimeout(missingClipTimerRef.current);
        setMissingVideoClip(name || 'video');
        resolvedClipSrcRef.current = { clip: name, avatar: selectedAvatar, src: fallbackSrc };
        shouldAutoPlayOnReadyRef.current = true;
        setIsVideoLoading(true);
        setBgVideo(fallbackSrc);
        setCurrentClip(name);
        setVideoKey(Date.now());
        setCanSendMessage(false);
        return;
      }
      pendingClipKeyRef.current = null;
      clearTimeout(missingClipTimerRef.current);
      setMissingVideoClip(name || 'video');
      setBgVideo(null);
      setVideoKey(Date.now());
      setCurrentClip(name);
      setIsVideoLoading(false);

      const isIntroMissing = introPrefetchKeys.includes(name);
      if (isIntroMissing) {
        setCanSendMessage(false);
        missingClipTimerRef.current = setTimeout(() => {
          startInterviewFromFlow();
        }, 1800);
      } else {
        setCanSendMessage(true);
        setInactivityTimer();
      }
      return;
    }
    setMissingVideoClip('');
    resolvedClipSrcRef.current = { clip: name, avatar: selectedAvatar, src };
    shouldAutoPlayOnReadyRef.current = true;
    setIsVideoLoading(true);
    setBgVideo(src);
    setCurrentClip(name);
    setVideoKey(Date.now());
  }, [videos, selectedAvatar, introPrefetchKeys, startInterviewFromFlow, setInactivityTimer, avatarCfg, speakByName]);

  useEffect(() => {
    changeVideoByNameRef.current = changeVideoByName;
  }, [changeVideoByName]);

  const handleReplayQuestion = useCallback(() => {
    const clip = lastQuestionClipRef.current;
    if (!clip) return;
    inactivitySteps.current = 0;
    setCanSendMessage(false);
    changeVideoByName(clip);
  }, [changeVideoByName]);

  const handleRestartInterview = useCallback(async () => {
    const confirmed = window.confirm('Deseja reiniciar a entrevista? Todas as respostas atuais serao apagadas.');
    if (!confirmed) return;

    try {
      const params = getParams();
      const entrevistaId = params.entrevistaId || user.entrevistaId || 'default';
      const denuncia = params.denuncia || user.denuncia || 'sem_denuncia';
      const company = params.company?._value || params.company || user.company || 'materdei';
      const interviewTypeKey = (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance');

      await resetInterviewState({
        company,
        interviewTypeKey,
        denuncia,
        entrevistaId,
        workflowStatus: DEFAULT_WORKFLOW_STATUS,
        interactionPhase: DEFAULT_INTERACTION_PHASE,
        statusModelVersion,
      });

      window.location.reload();
    } catch (err) {
      console.error('Erro ao reiniciar entrevista:', err);
      setModalType('error');
      setModalMessage('Nao foi possivel reiniciar a entrevista agora. Tente novamente.');
      setShowModal(true);
    }
  }, [interviewType, user.company, user.denuncia, user.entrevista, user.entrevistaId]);


  useEffect(() => {
    if (!isScriptLoaded) return;
    if (pendingFirstQuestionRef.current && !askedQ1Ref.current) {
      pendingFirstQuestionRef.current = false;
      startInterviewFromFlow();
    }
  }, [isScriptLoaded, startInterviewFromFlow]);

  const handleResumeClick = useCallback(() => {
    // O vídeo é sempre best-effort aqui: muitas perguntas ainda não têm vídeo gravado
    // (fallback __video_em_producao__), e mesmo quando bgVideo cai pro vídeo de intro
    // (bootstrap usa `prev || introVideo`), o play() pode rejeitar (elemento ainda
    // carregando, sem fonte de fato disponível etc.). Antes, o catch abaixo REARMAVA
    // o overlay (`setShowResumeOverlay(true)`), travando o candidato pra sempre nessa
    // tela depois de um F5 — sem nenhuma forma de continuar a entrevista. Liberar o
    // chat não pode depender de o vídeo tocar com sucesso.
    const releaseOverlay = () => {
      setShouldShowResumeOnLoad(false);
      setShowResumeOverlay(false);
      setWorkflowStatus('em_andamento');
    };
    if (!videoRef.current || !bgVideo) {
      releaseOverlay();
      return;
    }
    requestFullscreen();
    videoRef.current.play().then(releaseOverlay).catch((err) => {
      console.error('Falha ao iniciar o vídeo através do overlay (seguindo sem vídeo):', err);
      releaseOverlay();
    });
  }, [bgVideo]);

  useEffect(() => () => {
    clearTimeout(missingClipTimerRef.current);
  }, []);

  useEffect(() => {
    if (pendingClipKeyRef.current && videos?.[pendingClipKeyRef.current]) {
      const name = pendingClipKeyRef.current;
      pendingClipKeyRef.current = null;
      const src = videos[name];
      resolvedClipSrcRef.current = { clip: name, avatar: selectedAvatar, src };
      shouldAutoPlayOnReadyRef.current = true;
      setBgVideo(src);
      setCurrentClip(name);
      setVideoKey(Date.now());
    }
  }, [videos, selectedAvatar]);

  const hydrateMessagesFromResults = useCallback((results) => {
    const base = [{ text: replacePlaceholders(interviewData.intro, user), type: 'ia', first: true, time: getSaoPauloTimestamp() }];
    (results || []).forEach((item, idx) => {
      const resolvedIndex = typeof item.qIndex === 'number'
        ? item.qIndex
        : perguntas.findIndex((p) => (p.q || '') === (item.question || ''));
      const qIndex = resolvedIndex >= 0 ? resolvedIndex : idx;
      const qObj = perguntas[qIndex] || {};
      const videoKey = item.video || item.videoClip || qObj.video || '';
      const questionText = item.question || qObj.q || '';

      if (questionText) {
        base.push({ text: questionText, type: 'ia', isQuestion: true, qIndex, videoKey, timestamp: item.timestamp });
      }
      if (item.userResponse) base.push({ text: item.userResponse, type: 'user', timestamp: item.timestamp });

      const followKey = item.followKey || '';
      const followText = followKey && qObj[followKey] ? qObj[followKey] : item.followUp || '';
      if (followText) {
        base.push({ text: followText, type: 'ia', isFollowUp: true, followForIndex: qIndex, followKey, timestamp: item.timestamp });
        if (item.followUpResponse) base.push({ text: item.followUpResponse, type: 'user', timestamp: item.timestamp });
      }
    });
    introMessageInitialized.current = true;
    return base;
  }, [perguntas, interviewData.intro, user, getSaoPauloTimestamp]);

  useEffect(() => {
    if (!isScriptLoaded || shouldShowResumeOnLoad) return;
    if (introMessageInitialized.current) return;
    if (!interviewData.intro) return;
    const introText = replacePlaceholders(interviewData.intro, user);
    if (!introText) return;
    setMessages([{ text: introText, type: 'ia', first: true, time: getSaoPauloTimestamp() }]);
    introMessageInitialized.current = true;
  }, [isScriptLoaded, shouldShowResumeOnLoad, interviewData.intro, user, getSaoPauloTimestamp]);

  // Retomar a partir do próximo bloco ainda não salvo
  useEffect(() => {
    if (!isScriptLoaded) return;
    const params = getParams();
    const bootstrapKey = [
      params.entrevistaId || user.entrevistaId || 'default',
      params.denuncia || user.denuncia || 'sem_denuncia',
      params.company?._value || params.company || user.company || 'materdei',
      (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance'),
      (new URLSearchParams(window.location.search).get('t') || '').toString().trim(),
    ].join('::');
    if (resumeBootstrapKeyRef.current === bootstrapKey) {
      return;
    }
    resumeBootstrapKeyRef.current = bootstrapKey;
    setResumeBootstrapDone(false);
    const loadSaved = async () => {
      try {
        const entrevistaId = params.entrevistaId || user.entrevistaId || 'default';
        const denuncia = params.denuncia || user.denuncia || 'sem_denuncia';
        const company = params.company?._value || params.company || user.company || 'materdei';
        const interviewTypeKey = (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance');
        const accessToken = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();
        const data = await fetchInterviewDocument({
          company,
          interviewTypeKey,
          denuncia,
          entrevistaId,
          accessToken
        });
        if (!data) return;
        const resolvedVersion = resolveStatusModelVersion(interviewTypeKey, data.statusModelVersion);
        setStatusModelVersion(resolvedVersion);
        const storedResults = Array.isArray(data.responses)
          ? data.responses
          : (Array.isArray(data.messages) ? data.messages : []);
        const completedBlocks = storedResults.length;
        if (perguntas.length > 0 && completedBlocks >= perguntas.length) {
          setShowFinishContainer(true);
          return;
        }
        if (storedResults.length) {
          setShouldShowResumeOnLoad(true);
          setShowResumeOverlay(true);
          setShouldRunTour(false);
          setTourCompleted(true);
        }
        const resumeIndexFromDoc = Number.isFinite(data.resumeQuestionIndex)
          ? data.resumeQuestionIndex
          : -1;
        const resumeIndexFromId = data.resumeQuestionId
          ? findQuestionIndexByVideo(perguntas, data.resumeQuestionId)
          : -1;
        const resumedVideo = storedResults.length ? determineNextQuestionId(storedResults, flowDefinition, perguntas) : null;
        const resumeIndexFromFlow = resumedVideo ? findQuestionIndexByVideo(perguntas, resumedVideo) : -1;
        const resumeIndex = resumeIndexFromDoc >= 0
          ? resumeIndexFromDoc
          : (resumeIndexFromId >= 0 ? resumeIndexFromId : resumeIndexFromFlow);
        const hasResumeState = storedResults.length > 0;
        const initialWorkflow = data.workflowStatus
          || (hasResumeState ? 'aguardando_retomada' : 'pendente_inicio');
        setWorkflowStatus(initialWorkflow);
        if (typeof data.interactionPhase === 'string' && data.interactionPhase.trim()) {
          setInteractionPhase(data.interactionPhase);
        }

        // Sem respostas e sem ponteiro de retomada: deixa o bootstrap inicial cuidar do intro
        // para evitar reiniciar o mesmo clipe quando este efeito conclui assincronamente.
        if (!hasResumeState) {
          return;
        }

        const nextIndexCandidate = resumeIndex >= 0 ? resumeIndex : Math.min(completedBlocks, perguntas.length - 1);
        const nextIndex = Math.min(nextIndexCandidate, perguntas.length - 1);

        const hydrated = hydrateMessagesFromResults(storedResults);
        setMessages(hydrated);
        setCurrentIndex(nextIndex);

        const nextQuestion = perguntas[nextIndex];
        if (nextQuestion) {
          const clipName = nextQuestion.video || '';
          addMessage(nextQuestion.q, 'ia', { isQuestion: true, qIndex: nextIndex, videoKey: clipName });
          if (clipName) changeVideoByName(clipName);
        }
      } catch (err) {
        console.error('Erro ao retomar entrevista:', err);
      } finally {
        setResumeBootstrapDone(true);
      }
    };
    loadSaved();
  }, [interviewType, perguntas, interviewData.intro, user, addMessage, changeVideoByName, setShowFinishContainer, hydrateMessagesFromResults, flowDefinition, isScriptLoaded, activeIntroClipKey]);

  // Conversão de palavras para números simples
    const wordsToNumber = (txt) => {
      const map = { zero: 0, um: 1, dois: 2, três: 3, tres: 3, quatro: 4, cinco: 5 };
      const words = (txt || '')
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter(Boolean);
      const converted = words.map((w) => (map[w] !== undefined ? String(map[w]) : w));
      if (converted.length === 1 && converted[0] === '1') return '1';
      return converted.join(' ');
    };

  const sanitizeName = (str = '') => (str || 'arquivo')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100) || 'arquivo';

  // Upload de evidências
  const handleFileSelection = (e) => {
    console.log('[EVIDENCE] Selecionando arquivos...', e?.target?.files?.length || 0);
    const filesArray = Array.from(e.target.files);
    if (!filesArray.length) return;
    console.log('[EVIDENCE] Arquivos selecionados:', filesArray.map(f => f.name));
    setSelectedFiles((prev) => [...prev, ...filesArray]);
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // permite selecionar o mesmo arquivo novamente
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadSelectedFiles = async () => {
    if (isUploadingEvidence) return;
    setIsUploadingEvidence(true);
    console.log('[EVIDENCE] Iniciando upload', selectedFiles.map(f => f.name));
    const { company } = getParams();
    const companyName = company?._value || company || user.company || 'materdei';
    const numeroDenuncia = getParams().denuncia || user.denuncia || 'sem_denuncia';
    const nomeDoEntrevistado = user.nome || 'anônimo';

    if (selectedFiles.length === 0) {
      setModalMessage('Nenhum arquivo selecionado para upload.');
      setModalType('error');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
      return;
    }
    try {
      const filesPayload = selectedFiles.map((file) => ({
        name: sanitizeName(file.name),
        contentType: file.type || 'application/octet-stream',
      }));

      const data = await requestEvidenceUploadUrls({
        company: companyName,
        denuncia: numeroDenuncia,
        nome: nomeDoEntrevistado,
        files: filesPayload,
      });
      console.log('[EVIDENCE] URLs retornadas:', data);

      const urls = data?.urls || [];
      const uploadPromises = urls.map(async ({ name, url }) => {
        const file = selectedFiles.find((f) => sanitizeName(f.name) === name);
        if (!file) return;
        const resp = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Upload falhou (${resp.status} ${resp.statusText}) ${text}`);
        }
      });

      await Promise.all(uploadPromises);
      setModalMessage('Arquivos enviados com sucesso!');
      setModalType('success');
      setSelectedFiles([]);
    } catch (error) {
      console.error('[EVIDENCE] Erro no upload:', error);
      console.error('Erro ao enviar arquivos:', error);
      setModalMessage('Erro ao enviar arquivos. Verifique permissões ou tente novamente.');
      setModalType('error');
    }
    setShowModal(true);
    setTimeout(() => setShowModal(false), 3000);
    setIsUploadingEvidence(false);
  };

  // Adaptação canais de denúncia no texto final
  const addCanaisDeDenuncia = (text, company) => {
    if (typeof company !== 'string') return text;
    const canais = canaisDeDenuncia[company.toLowerCase()];
    if (!canais) return text;
    const canaisText = `${canais.site} ou ${canais.telefone}`;
    // Para Materdei, o texto "fim" já não cita SADA; não precisamos substituir nada específico.
    return text.replace(/(\bwww\.[^ ]+|\b\d{4,} \d{3} \d{4})/g, canaisText);
  };

  const buildResultsFromMessages = useCallback((messagesSource) => {
    const results = [];
    let currentQ = null;
    let pendingFollowUp = null;
    const msgs = messagesSource || [];

    for (const msg of msgs) {
      if (msg.type === 'ia' && msg.isQuestion) {
        const messageId = msg.questionId || msg.videoKey || '';
        const qIndexFromFlow = flowIndexMap.get(messageId);
        const qIndexFromId = findQuestionIndexByVideo(perguntas, messageId);
        const qIndex = Number.isFinite(qIndexFromFlow)
          ? qIndexFromFlow
          : (qIndexFromId >= 0
            ? qIndexFromId
            : (typeof msg.qIndex === 'number'
              ? msg.qIndex
              : perguntas.findIndex((p) => (p.q || '') === (msg.text || ''))));
        const qObj =
          findQuestionById(perguntas, messageId) ||
          perguntas[qIndex] ||
          {};
        currentQ = {
          qIndex,
          // Regras de fluxo usam id logico (q1, q1r1...).
          questionId: qObj.id || qObj.video || messageId || '',
          video: qObj.video || msg.videoKey || '',
          userResponse: '',
          followKey: '',
          followUpResponse: '',
          timestamp: msg.timestamp || msg.time || getSaoPauloTimestamp(),
        };
        pendingFollowUp = null;
      } else if (msg.type === 'ia' && msg.isFollowUp) {
        const target = currentQ || results.find((r) => r.qIndex === msg.followForIndex);
        if (target) {
          target.followKey = msg.followKey || target.followKey || '';
          pendingFollowUp = target;
        }
      } else if (msg.type === 'user') {
        if (pendingFollowUp) {
          pendingFollowUp.followUpResponse = msg.text;
          pendingFollowUp = null;
          continue;
        }

        if (currentQ && !currentQ.userResponse) {
          const qObj =
            findQuestionById(perguntas, currentQ.questionId) ||
            perguntas[currentQ.qIndex] ||
            {};
          const normalized = normalizeAnswerForQuestion(qObj, msg.text);
          if (!normalized.valid) {
            // Ignora respostas inválidas (não registra no backend)
            continue;
          }
          currentQ.userResponse = normalized.normalized;
          currentQ.timestamp = msg.timestamp || msg.time || getSaoPauloTimestamp();
          results.push(currentQ);
        }
      }
    }

    return sanitizeResponsesForSave(results);
  }, [perguntas, getSaoPauloTimestamp, flowIndexMap]);

  // Envio para Firestore
  const sendForm = useCallback(async (lastQuestionObj, statusOverride = {}) => {
    const params = getParams();
    const entrevistaId = params.entrevistaId || user.entrevistaId || 'default';
    const denuncia = params.denuncia || user.denuncia || 'sem_denuncia';
    const company = params.company?._value || params.company || user.company || 'materdei';
    const interviewTypeKey = (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance');

    const results = buildResultsFromMessages(messages);
    if (lastQuestionObj) {
      const resolvedIndex = Number.isFinite(lastQuestionObj.qIndex)
        ? lastQuestionObj.qIndex
        : (perguntas.length - 1);
      const resolvedVideo = lastQuestionObj.video || perguntas[resolvedIndex]?.video || '';
      const entry = {
        ...lastQuestionObj,
        qIndex: resolvedIndex,
        video: resolvedVideo,
        followUp: '',
        followUpResponse: lastQuestionObj.followUpResponse || '',
        userResponse: lastQuestionObj.userResponse || ''
      };
      const existingIndex = results.findIndex((item) => item.qIndex === entry.qIndex);
      if (existingIndex >= 0) {
        results[existingIndex] = {
          ...results[existingIndex],
          ...entry,
          followKey: results[existingIndex].followKey || entry.followKey,
          followUpResponse: results[existingIndex].followUpResponse || entry.followUpResponse,
          userResponse: entry.userResponse || results[existingIndex].userResponse
        };
      } else {
        results.push(entry);
      }
    }

    try {
      const introMessage = messages.find(m => m.first && m.type === 'ia');
      const introTime = introMessage ? (introMessage.time || introMessage.timestamp) : null;
      const endTime = getSaoPauloTimestamp();

      const resolvedStatusVersion = resolveStatusModelVersion(interviewTypeKey, statusModelVersion);
      setStatusModelVersion(resolvedStatusVersion);
      // statusOverride existe porque setWorkflowStatus/setInteractionPhase (React state) ainda não
      // propagaram no momento em que endInterview chama sendForm na mesma execução síncrona — sem
      // isso, o closure abaixo grava o valor ANTERIOR (ex.: "em_andamento" em vez de "concluido").
      const statusPayload = {
        workflowStatus: statusOverride.workflowStatus ?? workflowStatus,
        interactionPhase: statusOverride.interactionPhase ?? interactionPhase,
        statusModelVersion: resolvedStatusVersion
      };

      const updateData = {
        nome: user.nome || '',
        cpf: user.cpf || '',
        email: user.email || '',
        cargo: user.cargo || '',
        data: user.data || '',
        tempoEmpresa: user.tempoEmpresa || '',
        empresa: user.empresa || '',
        setor: user.setor || '',
        liderança: user.liderança || '',
        responses: results,
        scriptVersion,
        inicioEntrevista: introTime,
        finalEntrevista: endTime,
        timestamp: getSaoPauloDate(),
        ...statusPayload
      };
      // RS1/NOW1: a página pública roda sem Firebase Auth de propósito — um setDoc direto
      // (saveInterviewResults) sempre batia em permission-denied aqui pro candidato, então a
      // entrevista nunca fechava mesmo com as respostas todas salvas (só o autosave de
      // progresso, que já passava pelo token via saveInterviewResponses, funcionava). Usa
      // merge pra criar/atualizar o documento de respostas.
      const accessToken = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();
      await saveInterviewFinalResults({
        company,
        interviewTypeKey,
        denuncia,
        entrevistaId,
        data: updateData,
        accessToken
      });

      // Emails
      try {
        let sendToUserAt = null;
        let sendToComplianceAt = null;
        await sendCompletionEmails({
          company,
          interviewType: interviewTypeKey,
          numeroDenuncia: denuncia,
          entrevistaId
        });
        sendToUserAt = getSaoPauloTimestamp();
        sendToComplianceAt = getSaoPauloTimestamp();
        await saveInterviewFinalResults({
          company,
          interviewTypeKey,
          denuncia,
          entrevistaId,
          data: { responses: results, sendToUserAt, sendToComplianceAt },
          accessToken
        });
      } catch (e) { console.error('Erro no envio de e-mails:', e); }

      setModalMessage('O formulário foi enviado com sucesso para a área do Compliance.');
      setModalType('success');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);

      return entrevistaId;
    } catch (error) {
      console.error('Erro ao atualizar o documento:', error);
      setModalMessage('Houve um erro ao enviar o formulário. Por favor, tente novamente.');
      setModalType('error');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    }
  }, [messages, user, interviewType, buildResultsFromMessages, getSaoPauloDate, getSaoPauloTimestamp, perguntas, scriptVersion, workflowStatus, interactionPhase, statusModelVersion]);

  // Salva parcial ao fim de cada pergunta principal
  const saveBlockProgress = useCallback(async (messagesSnapshot = messages) => {
    try {
      const params = getParams();
      const entrevistaId = params.entrevistaId || user.entrevistaId || 'default';
      const denuncia = params.denuncia || user.denuncia || 'sem_denuncia';
      const company = params.company?._value || params.company || user.company || 'materdei';
      const interviewTypeKey = (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance');
      const accessToken = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();
      const results = buildResultsFromMessages(messagesSnapshot);

      const resolvedStatusVersion = resolveStatusModelVersion(interviewTypeKey, statusModelVersion);
      setStatusModelVersion(resolvedStatusVersion);
      await saveInterviewResponses({
        company,
        interviewTypeKey,
        denuncia,
        entrevistaId,
        responses: results,
        scriptVersion,
        workflowStatus,
        interactionPhase,
        statusModelVersion: resolvedStatusVersion,
        accessToken
      });
    } catch (err) {
      console.error('Erro ao salvar progresso da entrevista:', err);
    }
  }, [interviewType, buildResultsFromMessages, messages, user.company, user.denuncia, user.entrevista, user.entrevistaId, scriptVersion, workflowStatus, interactionPhase, statusModelVersion]);

  const resolveFinalVideoKey = useCallback(() => {
    const slug = normalizePathSegment(companyKey || user.company || '');
    const candidates = [
      slug ? ('fim-' + slug) : '',
      'final',
      'fim',
    ].filter(Boolean);
    return candidates.find((key) => videos?.[key]) || '';
  }, [companyKey, user.company, videos]);

  const persistResumeTarget = useCallback(async (index) => {
    if (!Number.isFinite(index) || index < 0 || index >= perguntas.length) return;
    const qObj = perguntas[index] || {};
    const resumeQuestionId = qObj.video || qObj.id || `q${index + 1}`;
    try {
      const params = getParams();
      const entrevistaId = params.entrevistaId || user.entrevistaId || 'default';
      const denuncia = params.denuncia || user.denuncia || 'sem_denuncia';
      const company = params.company?._value || params.company || user.company || 'materdei';
      const interviewTypeKey = (interviewType || params.interviewType || user.entrevista || 'pesquisa-de-compliance');
      const accessToken = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();
      await saveResumeTarget({
        company,
        interviewTypeKey,
        denuncia,
        entrevistaId,
        resumeQuestionId,
        resumeQuestionIndex: index,
        accessToken
      });
    } catch (err) {
      console.error('Erro ao salvar próxima pergunta para retomada:', err);
    }
  }, [interviewType, perguntas, user.company, user.denuncia, user.entrevista, user.entrevistaId]);

  const endInterview = useCallback(async (userResponse = '', options = {}) => {
    const { playFinalVideo = true, addFinalMessage = true, showFinishOnComplete = false } = options;
    try {
      const { company: companyNameParam } = getParams();
      const resolvedCompanyName = companyNameParam?._value || companyNameParam || user.company || 'materdei';

      const currentQuestion = perguntas[currentIndex] || {};
      const currentQuestionId = currentQuestion.id || currentQuestion.video || '';
      const qIndexFromFlow = flowIndexMap.get(currentQuestionId);
      const resolvedIndex = Number.isFinite(qIndexFromFlow) ? qIndexFromFlow : currentIndex;
      const lastQuestion = {
        qIndex: resolvedIndex,
        questionId: currentQuestionId,
        video: currentClip || currentQuestion.video || resolveFinalVideoKey(),
        followKey: '',
        followUpResponse: '',
        userResponse,
        timestamp: getSaoPauloTimestamp()
      };

      const entrevistaId = getParams().entrevistaId || user.entrevistaId;
      let hasPlayableFinalVideo = false;
      if (playFinalVideo) {
        const finalVideoKey = resolveFinalVideoKey();
        const finalVideoSrc = finalVideoKey ? videos[finalVideoKey] : null;
        if (finalVideoSrc) {
          changeVideo(finalVideoSrc);
          setCurrentClip(finalVideoKey);
          hasPlayableFinalVideo = true;
        }
      }

      if (addFinalMessage) {
        let finalMsg = replacePlaceholders(interviewData.fim, user).replace('{{doc}}', entrevistaId || '');
        finalMsg = addCanaisDeDenuncia(finalMsg, resolvedCompanyName);
        addMessage(finalMsg, 'ia');
      }

      if (hasPlayableFinalVideo) {
        setIsFinishVideo(true);
      } else {
        setIsFinishVideo(false);
        setShowFinishContainer(true);
      }
      if (showFinishOnComplete) {
        setShowFinishContainer(true);
      }

      setWorkflowStatus('concluido');

      await sendForm(lastQuestion, { workflowStatus: 'concluido' });
    } catch (error) {
      console.error('Erro ao finalizar a entrevista:', error);
      addMessage('Desculpe, ocorreu um erro ao enviar seu formulário. Por favor, tente novamente.', 'ia');
    }
  }, [addMessage, changeVideo, currentClip, currentIndex, getSaoPauloTimestamp, interviewData, perguntas, resolveFinalVideoKey, sendForm, setShowFinishContainer, user, videos, flowIndexMap]);

  // ===== Fluxo da entrevista =====
  // Mantemos um ponteiro de fluxo que respeita as condições do Q2
  const getNextIndexFromFlow = useCallback((messagesSnapshot) => {
    const results = buildResultsFromMessages(messagesSnapshot || messages);
    const nextQuestionId = determineNextQuestionId(results, flowDefinition, perguntas);
    const currentQuestion = perguntas[currentIndex];
    logFlowEvent('flow.next', {
      currentIndex,
      currentQuestionId: currentQuestion?.id || currentQuestion?.video || null,
      nextQuestionId
    });
    if (!nextQuestionId) {
      const sequentialNext = currentIndex + 1;
      return sequentialNext < perguntas.length ? sequentialNext : -1;
    }
    const nextIndex = findQuestionIndexByVideo(perguntas, nextQuestionId);
    if (nextIndex < 0) {
      logFlowEvent('flow.nextIndex.miss', {
        nextQuestionId,
        questionCount: perguntas.length,
        sampleIds: perguntas.slice(0, 10).map((q) => q.id || q.video || '')
      });
    }
    logFlowEvent('flow.nextIndex', { nextQuestionId, nextIndex });
    return nextIndex;
  }, [buildResultsFromMessages, flowDefinition, perguntas, messages, currentIndex]);

  // Enviar mensagem
    const sendMessage = async (e) => {
      e.preventDefault();
      if (text.trim() === '' || !canSendMessage) return;
      if (isListening) {
        toggleListening();
      }

    const newMessages = [...messages, { text, type: 'user', timestamp: getSaoPauloTimestamp() }];
    setMessages(newMessages);
    await handleUserResponse(text.trim(), newMessages);
      setText('');
      resetTranscript();
      clearTimeout(inactivityTimer.current);
      setInactivityTimer();
    };

    const sendQuickAnswer = async (value) => {
      if (!canSendMessage) return;
      if (isListening) {
        toggleListening();
      }
      const answerText = String(value);
      const newMessages = [...messages, { text: answerText, type: 'user', timestamp: getSaoPauloTimestamp() }];
      setMessages(newMessages);
      await handleUserResponse(answerText, newMessages);
      setText('');
      resetTranscript();
      clearTimeout(inactivityTimer.current);
      setInactivityTimer();
    };

  const handleUserResponse = useCallback(async (userInput, messagesSnapshot = messages) => {
    const msgs = messagesSnapshot || messages;
    const qObj = perguntas[currentIndex];
    if (qObj?.requiresResponse === false) {
      return;
    }

    // Se ja existe follow-up pendente para a pergunta atual, esta resposta
    // e a resposta do follow-up (nao revalida como resposta principal 1..5/sim-nao).
    if (pendingFollowUpState && pendingFollowUpState.qIndex === currentIndex) {
      logFlowEvent('followup.answer.received', {
        qIndex: currentIndex,
        id: qObj?.id,
        video: qObj?.video,
        followKey: pendingFollowUpState.followKey || '',
        input: userInput
      });
      setPendingFollowUpState(null);
      await saveBlockProgress(msgs);
      const nextIdx = getNextIndexFromFlow(msgs);
      if (nextIdx >= 0) {
        persistResumeTarget(nextIdx);
        advanceToQuestion(nextIdx);
      } else {
        endInterview(userInput);
      }
      return;
    }

    logFlowEvent('answer.received', {
      qIndex: currentIndex,
      id: qObj?.id,
      video: qObj?.video,
      input: userInput
    });

    const validation = normalizeAnswerForQuestion(qObj, userInput);
    if (!validation.valid) {
      const invalidConfig = validation.invalid;
      if (invalidConfig) {
        const invalidMessage = invalidConfig?.message || 'Resposta inválida.';
        addMessage(invalidMessage, 'ia');
        const invalidVideo = invalidConfig?.video;
        if (invalidVideo && videos[invalidVideo]) changeVideo(videos[invalidVideo]);
      }
      logFlowEvent('answer.invalid', {
        qIndex: currentIndex,
        id: qObj?.id,
        video: qObj?.video,
        input: userInput
      });
      return;
    }
    logFlowEvent('answer.normalized', {
      qIndex: currentIndex,
      id: qObj?.id,
      video: qObj?.video,
      answer: validation.normalized
    });


    const resolveFollowUpKey = () => {
      if (!qObj) return '';
      const hasR1 = typeof qObj.r1 === 'string' && qObj.r1.trim().length > 0;
      const hasR2 = typeof qObj.r2 === 'string' && qObj.r2.trim().length > 0;
      const hasR3 = typeof qObj.r3 === 'string' && qObj.r3.trim().length > 0;
      if (!hasR1 && !hasR2 && !hasR3) return '';

      if (qObj.answerType === 'yesno') {
        if (validation.normalized === 'sim' && hasR1) return 'r1';
        if ((validation.normalized === 'nao' || validation.normalized === 'não') && hasR2) return 'r2';
        return '';
      }

      if (qObj.type === 'scale' || qObj.scale) {
        const score = Number(validation.normalized);
        if (!Number.isFinite(score)) return '';
        if (score <= 3 && hasR1) return 'r1';
        if (score === 4 && hasR2) return 'r2';
        if (score === 5 && hasR3) return 'r3';
      }
      return '';
    };

    const followKey = resolveFollowUpKey();
    if (followKey) {
      const followText = qObj?.[followKey];
      if (followText) {
        const baseKey = qObj.video || qObj.id || ("q" + (currentIndex + 1));
        const followVideoKey = baseKey + followKey;
        addMessage(followText, 'ia', {
          isFollowUp: true,
          followForIndex: currentIndex,
          followKey,
          videoKey: followVideoKey
        });
        if (changeVideoByNameRef.current) {
          changeVideoByNameRef.current(followVideoKey);
        }
        setPendingFollowUpState({ qIndex: currentIndex, followKey });
        return;
      }
    }

    // Se chegamos aqui: pergunta sem r1/r2/r3
    await saveBlockProgress(msgs);
    const nextIdx = getNextIndexFromFlow(msgs);
    if (nextIdx >= 0) {
      persistResumeTarget(nextIdx);
      advanceToQuestion(nextIdx);
    } else {
      endInterview(validation.normalized);
    }
  }, [currentIndex, perguntas, endInterview, videos, addMessage, advanceToQuestion, changeVideo, saveBlockProgress, messages, getNextIndexFromFlow, persistResumeTarget, pendingFollowUpState]);

  // Input change
  const handleChange = (e) => {
    setText(e.target.value);
    clearTimeout(inactivityTimer.current);
    setInactivityTimer();
  };

    const currentQuestionObj = perguntas[currentIndex];
    const askedQuestionIds = useMemo(() => {
      const asked = [];
      const seen = new Set();
      (messages || []).forEach((msg) => {
        if (msg?.type !== 'ia') return;
        const hasQuestionMarker = msg?.isQuestion || Number.isFinite(msg?.qIndex) || !!msg?.questionId || !!msg?.videoKey;
        if (!hasQuestionMarker) return;
        const byIndex = Number.isFinite(msg?.qIndex) ? perguntas[msg.qIndex] : null;
        const resolvedQuestion =
          byIndex ||
          findQuestionById(perguntas, msg?.questionId || msg?.videoKey) ||
          null;
        if (resolvedQuestion?.requiresResponse === false) return;
        const id =
          resolvedQuestion?.id ||
          resolvedQuestion?.video ||
          msg?.questionId ||
          msg?.videoKey ||
          null;
        if (!id || seen.has(id)) return;
        seen.add(id);
        asked.push(id);
      });
      return asked;
    }, [messages, perguntas]);

    const interviewProgress = useMemo(() => {
      const results = buildResultsFromMessages(messages);
      return calculateInterviewProgress({
        responses: results,
        flow: flowDefinition,
        questions: perguntas,
        isFinished: isFinishVideo,
        askedQuestionIds
      });
    }, [messages, perguntas, flowDefinition, buildResultsFromMessages, isFinishVideo, askedQuestionIds]);

    const interviewProgressPercent = interviewProgress?.percent ?? 0;
    const interviewProgressTooltip = `${interviewProgress?.answered ?? 0}/${interviewProgress?.total ?? 0}`;
    const questionTypeNormalized = String(currentQuestionObj?.type || '').toLowerCase();
    const answerTypeNormalized = String(currentQuestionObj?.answerType || '').toLowerCase();
    const isScaleQuestion =
      questionTypeNormalized === 'scale' ||
      !!currentQuestionObj?.scale;
    const isYesNoQuestion =
      answerTypeNormalized === 'yesno' ||
      answerTypeNormalized === 'yes_no' ||
      answerTypeNormalized === 'simnao' ||
      answerTypeNormalized === 'sim_nao' ||
      answerTypeNormalized === 'boolean';

    const scaleMin = Number(currentQuestionObj?.scale?.min ?? 1);
    const scaleMax = Number(currentQuestionObj?.scale?.max ?? 5);
    const quickOptions = isScaleQuestion
      ? Array.from({ length: Math.max(1, scaleMax - scaleMin + 1) }, (_, i) => ({ label: String(scaleMin + i), value: scaleMin + i }))
      : (isYesNoQuestion
        ? [
          { label: 'Sim', value: 'sim' },
          { label: 'Não', value: 'nao' }
        ]
        : []);
    const showTextInput = !(isScaleQuestion || isYesNoQuestion);
    const showMic = showTextInput;
    const isIntroPhase = Boolean(currentClip && introPrefetchKeys.includes(currentClip));
    const isFinalClipMatch = Boolean(
      currentClip &&
      (
        (finalClipKey && currentClip === finalClipKey) ||
        currentClip === 'final' ||
        currentClip === 'fim' ||
        (finalSlug && currentClip === `fim-${finalSlug}`)
      )
    );
    const isFinalPhase = isFinishVideo || isFinalClipMatch;
    const hideInteractionUI = isIntroPhase || isFinalPhase;
    const visibleQuickOptions = hideInteractionUI ? [] : quickOptions;
    const visibleShowTextInput = hideInteractionUI ? false : showTextInput;
    const visibleShowMic = hideInteractionUI ? false : showMic;
    const showAvatarPickerInTour = !!(interviewConfig?.features?.avatarPicker && Array.isArray(interviewConfig?.avatars) && interviewConfig.avatars.length > 1);

    // UI e Tour: segue a ordem visual (linha superior da esquerda para a direita, depois linha inferior)
    const tourSteps = useMemo(() => {
      const topRowSteps = [
        { target: '[data-tour="step-cc"]', content: 'Habilitar a legenda do vídeo.', disableBeacon: true },
        { target: '[data-tour="step-speed"]', content: 'Ajustar a velocidade do vídeo.', disableBeacon: true },
      ];
      if (showAvatarPickerInTour) {
        topRowSteps.push({ target: '[data-tour="step-avatar"]', content: 'Escolher o avatar da entrevista.' });
      }
      topRowSteps.push(
        { target: '[data-tour="step-evidence"]', content: 'Anexar as suas evidências.' },
        { target: '[data-tour="step-fullscreen"]', content: 'Abrir a entrevista em tela cheia.' }
      );

      const bottomRowSteps = [];
      if (showTextInput) {
        bottomRowSteps.push(
          { target: '[data-tour="step-input"]', content: 'Digite aqui sua resposta.' },
          { target: '[data-tour="step-send"]', content: 'Enviar a sua resposta.' }
        );
      }
      if (showMic) {
        bottomRowSteps.push({ target: '[data-tour="step-mic"]', content: 'Ativar a transcrição por voz.' });
      }

      return [...topRowSteps, ...bottomRowSteps];
    }, [showAvatarPickerInTour, showTextInput, showMic]);

    return (
    <>
      {shouldRunTour && (
      <Joyride
        steps={tourSteps}
        run={true}
        continuous
        showSkipButton
        showProgress
        callback={handleTourCallback}
        locale={{ back: 'Voltar', close: 'Fechar', last: 'Finalizar', next: 'Próximo', nextLabelWithProgress: 'Próximo (Etapa {step} de {steps})', skip: 'Pular' }}
        styles={{
          options: { zIndex: 10000, primaryColor: '#28a745', textColor: '#333', backgroundColor: '#ffffff', arrowColor: '#ffffff', fontFamily: "'Roboto', sans-serif", borderRadius: 16, transition: 'all 0.4s ease-in-out' },
          tooltip: { borderRadius: 16, boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2)', padding: '20px 24px', fontSize: '18px', fontWeight: '500', transition: 'all 0.4s ease', fontFamily: "'Roboto', sans-serif" },
          buttonNext: { backgroundColor: '#28a745', color: '#fff', borderRadius: '8px', padding: '10px 18px', fontWeight: 'bold', fontSize: '16px', transition: 'background-color 0.3s ease', fontFamily: "'Roboto', sans-serif" },
          buttonBack: { color: '#6c757d', fontSize: '14px', fontWeight: 'bold', transition: 'color 0.3s ease', fontFamily: "'Roboto', sans-serif" },
          buttonClose: { color: '#000', fontSize: '16px', fontWeight: 'bold', fontFamily: "'Roboto', sans-serif" },
          spotlight: { borderRadius: '12px', boxShadow: '0 0 0 6px rgba(40, 167, 69, 0.4)', animation: 'pulse 2s infinite ease-in-out' }
        }}
      />
      )}

      <InterviewLayout
          controlsProps={{
          features: interviewConfig.features,
          avatars: interviewConfig.avatars,
          selectedAvatar,
          onAvatarChange: setSelectedAvatar,
          tourId: 'step-speed',
          showCaptions,
          onToggleCaptions: () => setShowCaptions((prev) => !prev),
          videoSpeed,
          onSpeedChange: handleSpeedChange
        }}
        progressPercent={interviewProgressPercent}
        progressTooltip={interviewProgressTooltip}
        evidenceProps={{ onClick: triggerFileSelect }}
        videoProps={avatarCfg?.visemeEnabled ? {
          ref: videoRef,
          key: videoKey,
          mode: 'viseme',
          avatarId: avatarCfg.id,
          mouthAssets: avatarCfg.mouthAssets,
          audioSrc: avatarSpeech?.audioSrc || null,
          visemes: avatarSpeech?.visemes || [],
          autoPlay: false,
          preload: "auto",
          onLoadStart: handleVideoLoadStart,
          onCanPlay: handleVideoCanPlay,
          onLoadedData: handleVideoCanPlay,
          onWaiting: handleVideoWaiting,
          onError: handleVideoError,
          onPlay: handleVideoPlay,
          onEnded: handleVideoEnd
        } : {
          ref: videoRef,
          key: videoKey,
          autoPlay: false,
          preload: "auto",
          controls: true,
          src: bgVideo,
          type: "video/mp4",
          onLoadStart: handleVideoLoadStart,
          onCanPlay: handleVideoCanPlay,
          onLoadedData: handleVideoCanPlay,
          onWaiting: handleVideoWaiting,
          onError: handleVideoError,
          onPlay: handleVideoPlay,
          onEnded: handleVideoEnd
        }}
        videoLoading={!!currentClip && isVideoLoading}
        videoMissing={!!missingVideoClip && !isVideoLoading}
        videoMissingText={missingVideoClip ? "Video em fase de producao" : "Carregando video..."}
        messages={messages}
        showCaptions={showCaptions}
        isPlaying={isVideoPlayingState}
          isListening={isListening}
          isTranscribing={isTranscribing}
          userInput={text}
          onInputChange={handleChange}
          onSend={sendMessage}
          onToggleMic={() => toggleListening(setText)}
          quickOptions={visibleQuickOptions}
          quickActivationKey={currentQuestionObj?.id || currentQuestionObj?.video || `q-${currentIndex}`}
          onQuickSelect={sendQuickAnswer}
          quickDisabled={!tourCompleted || !canSendMessage}
          showTextInput={visibleShowTextInput}
          showMic={visibleShowMic}
          showReload={!isVideoPlayingState && !isFinishVideo && !!lastQuestionClipRef.current}
          onReload={handleReplayQuestion}
          onRestartInterview={handleRestartInterview}
          sendDisabled={!canSendMessage || !text.trim()}
          layoutOptions={{
            pageWidth: '500px',
            pageHeight: '720px',
            videoWidth: '800px',
            messageMaxHeight: '200px',
            messageMarginBottom: '90px'
          }}
      />

      {showResumeOverlay && (
        <ResumeOverlay onClick={handleResumeClick}>
          <ResumeCard>
            <h2>Retomar entrevista</h2>
            <p>A entrevista está sendo carregada de onde você parou. Clique aqui quando estiver pronto para continuar.</p>
          </ResumeCard>
        </ResumeOverlay>
      )}

      <input
        id="file-upload"
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelection}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
      />

      {selectedFiles.length > 0 && (
      <EvidenceContainer>
        <div>
          {selectedFiles.map((file, index) => (
            <SelectedFile key={index}>
              <RemoveButton onClick={() => removeFile(index)}>
                🗑️
              </RemoveButton>
              <span>{file.name}</span>
            </SelectedFile>
          ))}
        </div>
        {selectedFiles.length > 0 && (
          <button
            type="button"
            onClick={uploadSelectedFiles}
            disabled={isUploadingEvidence}
          >
            {isUploadingEvidence ? 'Enviando...' : 'Enviar'}
          </button>
        )}
      </EvidenceContainer>
      )}

      <Modal $show={showModal} $type={modalType}>
        <ModalMessage $type={modalType}>{modalMessage}</ModalMessage>
      </Modal>
    </>
  );
}

// ==== Styled components usados no fluxo (evidências e modal) ====
const EvidenceContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: min(360px, 90vw);
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  padding: 14px;
  border-radius: 14px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.28);
  pointer-events: auto;

  button {
    display: inline-block;
    padding: 10px 14px;
    background-color: #28a745;
    color: white;
    font-size: 14px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    font-family: 'Roboto', sans-serif;
  }
  button:hover { background-color: #218838; transform: translateY(-1px); }

  @media only screen and (max-width: 640px) {
    top: 72px;
  }
`;

const ResumeOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ResumeCard = styled.div`
  background: #fff;
  padding: 24px 28px;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  text-align: center;
  width: 320px;
  font-family: 'Roboto', sans-serif;

  h2 {
    margin: 0 0 8px;
    font-size: 18px;
  }

  p {
    margin: 0 0 16px;
    font-size: 14px;
    color: #333;
  }

  button {
    background: #28a745;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
  }
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px;
  border-radius: 8px;
  margin-bottom: 5px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  width: 100%;
  background: rgba(255,255,255,0.1);
`;

const RemoveButton = styled.button`
  background: transparent !important;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 6px;
  border-radius: 6px;
  margin-right: 8px;
  transition: background 0.2s ease, transform 0.1s;
  &:hover { background: rgba(255,255,255,0.15) !important; transform: translateY(-1px); }
`;

const Modal = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  padding: 30px;
  padding-right: 40px;
  background: #FFF;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  border-left: 5px solid ${props => props.$type === 'success' ? '#40C057' : '#FA5252'};
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: opacity 0.5s, visibility 0.5s;
  opacity: ${props => props.$show ? '1' : '0'};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
`;

const ModalMessage = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  color: #333;
`;











































